import * as THREE from 'three/webgpu';
import {PATTERN_SKIP_MARKER_MAGENTA_COLOR} from '../../constants/pattern-preview-visual';
import {SKETCH_CONTROL_POINT_DEFAULT_SCALE} from '../../constants/sketch-point-visual';
import {LineMaterialFactory} from '../../material/LineMaterialFactory';
import {PatternSkipMarkerMaterialFactory} from '../../material/PatternSkipMarkerMaterialFactory';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import {isAbstractSketchEdgeObject, isSketchPointObject} from '../../mesh/sketch';
import type {
    SketchCircularPatternSkipMarkerFrame,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternSkipInstancePreview';

/** 跳过实例选择点相对草图控制点的尺寸倍率。 */
const SKIP_MARKER_CONTROL_POINT_SCALE_MULTIPLIER = 2;

/** 圆周草图阵列可跳过实例标记颜色。 */
const AVAILABLE_SKIP_MARKER_COLOR = 0xff0000;

/**
 * 圆周草图阵列跳过实例预览对象。
 *
 * 职责：
 * 1. 在每个非源实例的半径锚点位置创建可拾取选择点；
 * 2. 维护选择点运行时对象到稳定实例索引的映射；
 * 3. 把当前可跳过的派生草图预览统一显示为黄色；
 * 4. 复用阵列跳过标记材质工厂的固定屏幕尺寸规则。
 *
 * 该对象只属于命令态 Viewer 覆盖层，不进入活动草图、持久化或历史。
 */
export class SketchCircularPatternSkipInstancePreview extends THREE.Group {
    /** 未跳过实例共享的红色独占预览材质。 */
    private readonly availableMarkerMaterial = PatternSkipMarkerMaterialFactory.create(
        AVAILABLE_SKIP_MARKER_COLOR,
        '圆周草图阵列-可跳过实例选择点材质',
    );

    /** 已跳过实例共享的洋红色独占预览材质。 */
    private readonly skippedMarkerMaterial = PatternSkipMarkerMaterialFactory.create(
        PATTERN_SKIP_MARKER_MAGENTA_COLOR,
        '圆周草图阵列-已跳过实例选择点材质',
    );

    /** 当前全部跳过实例选择点。 */
    private readonly markerObjects: THREE.Sprite[] = [];

    /** 选择点运行时 UUID 到稳定实例索引映射。 */
    private readonly instanceIndexByMarkerId = new Map<string, number>();

    /**
     * 创建跳过实例预览对象。
     */
    public constructor() {
        super();
        this.name = '圆周草图阵列-可跳过实例预览';
        this.renderOrder = 96;
    }

    /**
     * 按当前完整分布更新选择点。
     *
     * @param frames 非源实例选择点参数。
     */
    public updateMarkers(frames: readonly SketchCircularPatternSkipMarkerFrame[]): void {
        this.clearMarkers();
        frames.forEach((frame) => {
            if (!Number.isInteger(frame.instanceIndex) || frame.instanceIndex <= 0) {
                return;
            }
            const marker = this.createMarker(frame);
            this.markerObjects.push(marker);
            this.instanceIndexByMarkerId.set(marker.uuid, frame.instanceIndex);
            this.add(marker);
        });
    }

    /**
     * 获取当前全部选择点对象。
     *
     * @returns 选择点列表副本。
     */
    public getMarkerObjects(): THREE.Sprite[] {
        return [...this.markerObjects];
    }

    /**
     * 从选择点或其子对象解析稳定实例索引。
     *
     * @param object 当前命中对象。
     * @returns 稳定实例索引；不是选择点时返回 null。
     */
    public resolveInstanceIndexByMarker(object: THREE.Object3D): number | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            const instanceIndex = this.instanceIndexByMarkerId.get(current.uuid);
            if (instanceIndex !== undefined) {
                return instanceIndex;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 将当前派生实例副本应用为黄色可跳过候选视觉。
     *
     * 副本线材质由克隆链独占，可在替换前释放；点材质来自共享工厂，只替换引用不销毁。
     *
     * @param roots 当前单个派生实例的副本根对象。
     */
    public applySelectableInstanceStyle(roots: readonly THREE.Object3D[]): void {
        const styledEdgeIds = new Set<string>();
        const styledPointIds = new Set<string>();
        roots.forEach((root) => {
            root.traverse((object) => {
                if (isAbstractSketchEdgeObject(object)) {
                    if (styledEdgeIds.has(object.uuid)) {
                        return;
                    }
                    styledEdgeIds.add(object.uuid);
                    const previousMaterial = object.material;
                    if (Array.isArray(previousMaterial)) {
                        previousMaterial.forEach((material) => material.dispose());
                    } else {
                        previousMaterial.dispose();
                    }
                    object.material = LineMaterialFactory.getReferenceSourceHighlightMaterial(object.baseStyle);
                    object.relatedSketchPoints.forEach((point) => {
                        this.applyPointStyle(point, styledPointIds);
                    });
                    return;
                }
                if (isSketchPointObject(object)) {
                    this.applyPointStyle(object, styledPointIds);
                }
            });
        });
    }

    /**
     * 释放当前预览资源。
     */
    public dispose(): void {
        this.clearMarkers();
        this.availableMarkerMaterial.dispose();
        this.skippedMarkerMaterial.dispose();
    }

    /**
     * 创建单个选择点。
     *
     * @param frame 当前实例选择点参数。
     * @returns 可直接加入预览组的选择点。
     */
    private createMarker(frame: SketchCircularPatternSkipMarkerFrame): THREE.Sprite {
        const material = frame.skipped
            ? this.skippedMarkerMaterial
            : this.availableMarkerMaterial;
        const marker = new THREE.Sprite(material);
        marker.name = `圆周草图阵列-可跳过实例-${frame.instanceIndex}`;
        marker.position.copy(frame.position);
        // 只比草图控制点大两倍，避免屏幕固定尺寸与世界缩放混用导致标记遮挡几何。
        marker.scale.setScalar(
            SKETCH_CONTROL_POINT_DEFAULT_SCALE * SKIP_MARKER_CONTROL_POINT_SCALE_MULTIPLIER,
        );
        marker.renderOrder = 97;
        return marker;
    }

    /**
     * 应用共享黄色点材质。
     *
     * @param point 当前副本控制点。
     * @param styledPointIds 已处理点 UUID 集合。
     */
    private applyPointStyle(point: THREE.Sprite, styledPointIds: Set<string>): void {
        if (styledPointIds.has(point.uuid)) {
            return;
        }
        styledPointIds.add(point.uuid);
        point.material = PointsMaterialFactory.getReferenceSourceHighlightMaterial();
    }

    /**
     * 清理全部选择点。
     *
     * 全部标记共用当前预览对象持有的两个状态材质，此处只解除场景引用。
     * 独占材质由 `dispose()` 在预览整体销毁时一次释放，避免参数刷新频繁重建 GPU 资源。
     */
    private clearMarkers(): void {
        this.markerObjects.forEach((marker) => {
            marker.parent?.remove(marker);
        });
        this.markerObjects.length = 0;
        this.instanceIndexByMarkerId.clear();
    }
}
